import nvitTolldata from './nb/nvit-tolldata.md';
import crossdockMottak from './nb/crossdock-mottak.md';
import pakkseddelFraCompass from './nb/pakkseddel-fra-compass.md';
import gs1128Etiketter from './nb/gs1-128-etiketter.md';

/**
 * Katalog over hjelpeartikler for lagersjef («Hjelp og veiledning»).
 *
 * VEDLIKEHOLD (se CLAUDE.md, «Hjelpeartikler»): hver artikkel lister koden (`sources`) og
 * innstillingene (`settings`) den beskriver. Endres noe av dette, skal artikkelen kontrolleres
 * i samme endring — oppdater teksten eller bump `lastVerified`. Artikler endres ellers bare når
 * brukeren ber om det. Plan: Claude md files/Plan-Hjelpesenter-Lagersjef.md.
 */
export interface HelpArticle {
  /** Unik, stabil id (brukes i lenker). */
  id: string;
  title: string;
  /** Kort ingress i artikkellisten. */
  summary: string;
  category: HelpCategory;
  /** Asset-URL til markdown-filen (norsk). */
  file: string;
  /** Innstillinger artikkelen beskriver (SettingKey). */
  settings: string[];
  /** Kode artikkelen beskriver, relativt til repo-roten. */
  sources: string[];
  /** Første versjon artikkelen gjelder for. */
  validFrom: string;
  /** Sist kontrollert mot koden (YYYY-MM-DD). */
  lastVerified: string;
}

export type HelpCategory = 'Frakt' | 'Plukk' | 'Pakk' | 'Mottak' | 'Lager' | 'Oppsett';

export const HELP_CATEGORY_ORDER: HelpCategory[] = ['Frakt', 'Plukk', 'Pakk', 'Mottak', 'Lager', 'Oppsett'];

export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: 'nvit-tolldata',
    title: 'NVIT — tolldata til transportøren',
    summary: 'Hva som må være på plass for at tolldata sendes automatisk for sendinger via Sverige eller Finland.',
    category: 'Frakt',
    file: nvitTolldata,
    settings: ['ta.nvitCustomsEnabled', 'picking.nvitZipRanges', 'ta.validateBeforeCompletion'],
    sources: [
      'BizClick.WMS.Core/Services/Customs/NvitResolver.cs',
      'BizClick.WMS.Core/Services/Customs/CustomsCodes.cs',
      'BizClick.WMS.Core/Services/Cargonizer/TaProviders/ConsignmentInputBuilder.cs',
      'BizClick.WMS.Core/Services/Cargonizer/TaProviders/NShift/NShiftMapper.cs',
      'BizClick.WMS.Core/Services/Cargonizer/TaProviders/Cargonizer/CargonizerMapper.cs',
      'BizClick.WMS.Core/Services/Cargonizer/TaProviders/AxiaFrakt/AxiaFraktMapper.cs',
      'BizClick.WMS.Core/Services/Cargonizer/TransportAdminIntegrationService.cs',
    ],
    validFrom: '1.54.0',
    lastVerified: '2026-10-27',
  },
  {
    id: 'crossdock-mottak',
    title: 'Crossdock i varemottak',
    summary: 'Legg varer med restordre rett på crossdock-lokasjon ved mottak — oppsett per lagersted og hva operatøren ser.',
    category: 'Mottak',
    file: crossdockMottak,
    settings: [],
    sources: [
      'BizClick.WMS.Core/Services/CrossDockSuggestionService.cs',
      'BizClick.WMS.Core/Services/CrossDockPutAwayService.cs',
      'BizClick.WMS.Data/Repositories/CrossDockRepository.cs',
      'BizClick.WMS.Core/Services/WarehouseService.cs',
      'BizClick.WMS.Web/src/components/warehouse/modules/receipt/hooks/useCrossDockSuggestion.ts',
      'BizClick.WMS.Web/src/components/warehouse/modules/receipt/CrossDockPanel.tsx',
      'BizClick.WMS.Web/src/components/warehouse/modules/receipt/hooks/useCrossDockCandidates.ts',
      'BizClick.WMS.Web/src/components/warehouse/modules/receipt/search/POrderLineBrowser.tsx',
      'BizClick.WMS.Web/src/components/warehouse/modules/administration/WarehouseApp.tsx',
    ],
    validFrom: '1.57.0',
    lastVerified: '2026-10-30',
  },
  {
    id: 'pakkseddel-fra-compass',
    title: 'Pakkseddel fra Compass',
    summary: 'La Compass skrive pakkseddelen selv ved avslutning — én per forsendelse, eller én per mottaker når plukket avsluttes direkte. Mal, skriver, flagg og hva som erstatter eksportfilen til ERP.',
    category: 'Pakk',
    file: pakkseddelFraCompass,
    settings: [
      'printing.autoPrintFromCompassPackingslipOnComplete',
      'DefaultReportId:10',
      'ExportShipmentFiles',
      'PrintSetupFromDMYesNo',
      'PackageSlipERPYesNo',
      'PackageSlipPrinterName',
    ],
    sources: [
      'BizClick.WMS.Core/Services/EventHandlers/PListEventHandlers.cs',
      'BizClick.WMS.Core/Services/PrintingService.cs',
      'BizClick.WMS.Core/Services/PListPrintInfoService.cs',
      'BizClick.WMS.Core/Services/PackingSlipRecipientGrouping.cs',
      'BizClick.WMS.Core/Services/PackingService.cs',
      'BizClick.WMS.Data/Repositories/LabelDataResolver.cs',
      'BizClick.WMS.Reporting/Generators/CustomLabelGenerator.cs',
      'BizClick.WMS.Web/src/components/warehouse/modules/packing/PackingDetailView.tsx',
      'BizClick.WMS.Web/src/components/warehouse/modules/administration/label-designer/PropertyPanel.tsx',
    ],
    validFrom: '1.58.0',
    lastVerified: '2026-11-04',
  },
  {
    id: 'gs1-128-etiketter',
    title: 'GS1-128-strekkoder på etiketter',
    summary: 'Hvordan plassholderne i en GS1-128-strekkode blir til AI-er, når GTIN blir (01) eller (02), og anbefalt oppbygging av pall-etiketten.',
    category: 'Oppsett',
    file: gs1128Etiketter,
    settings: [],
    sources: [
      'BizClick.WMS.Reporting/Common/Gs1128ElementBuilder.cs',
      'BizClick.WMS.Reporting/Common/BarcodeHelper.cs',
      'BizClick.WMS.Reporting/Generators/CustomLabelGenerator.cs',
      'BizClick.WMS.Data/Repositories/LabelDataResolver.cs',
      'BizClick.WMS.Web/src/components/warehouse/modules/administration/label-designer/PropertyPanel.tsx',
    ],
    validFrom: '1.63.1',
    lastVerified: '2026-10-28',
  },
];
